import React from 'react';

import {
    formatLinkBlockToUrl,
    formatStudioCapacity
} from '$utilities/format.js';

import { IconArrowRight } from '$partials/icon/index.js';
import { Image } from '$partials/media/index.js';
import { isLocalURL } from '$utilities/browser';
import { PageShell } from '$partials/global/index.js';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/pages.d.ts').StudioListingPage}
 */
const StudioListingPage = (props) => {
    const page = props.data?.page;

    const studios = page?.children?.edges
        ?.map((edge) => edge.node)
        .filter((studio) => studio?.id) || [];

    const ctaHref = formatLinkBlockToUrl(page?.ctaLink);
    const isCtaExternal = ctaHref && !isLocalURL(ctaHref);

    return (
        <PageShell
            banner
            footer={props.data?.footer}
            header={props.data?.header}>
            <div className='layout-container bg-neutral-00'>
                <div className='py-16 sm:py-40'>
                    <div className='layout-grid flex-col sm:flex-row'>
                        {/* Page header and filters */}
                        <div className='w-full sm:w-4/12 lg:w-3/12'>
                            <div className='sm:sticky sm:top-110'>
                                <div className='layout-grid-col'>
                                    <h1 className='type-style-heading'>
                                        {page?.title}
                                    </h1>

                                    {page?.introduction && (
                                        <div
                                            className='rich-text type-style-heading'
                                            dangerouslySetInnerHTML={{ __html: page.introduction }} />
                                    )}
                                </div>

                                {studios.length > 0 && (
                                    <div className='w-full sm:w-3/4 lg:w-2/3 mt-24 sm:mt-32 layout-grid-col'>
                                        <table className='w-full'>
                                            <caption className='sr-only'>The studios, with their capacities</caption>

                                            <tbody>
                                                {studios.map((studio) => (
                                                    <tr
                                                        className='border-t border-neutral-400'
                                                        key={studio.id}>
                                                        <th
                                                            className='w-4/8 py-12 pr-8 type-style-body text-left'
                                                            scope='row'>
                                                            <a href={`#${studio.slug}`}>
                                                                {studio.title}
                                                            </a>
                                                        </th>

                                                        <td className='py-12 type-style-body'>
                                                            {studio.area}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {(page?.ctaLabel && ctaHref) && (
                                    <p className='layout-grid-col mt-24 sm:mt-32'>
                                        <a
                                            className='button type-style-body cursor-pointer'
                                            href={ctaHref}
                                            rel={isCtaExternal ? 'noopener noreferrer' : undefined}
                                            target={isCtaExternal ? '_blank' : undefined}>
                                            {page.ctaLabel}

                                            <span
                                                aria-hidden={true}
                                                className='ml-5'>
                                                <IconArrowRight
                                                    height={20}
                                                    width={20} />
                                            </span>
                                        </a>
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* A row per studio */}
                        {studios.length > 0 && (
                            <div className='w-full sm:w-8/12 lg:w-9/12 layout-grid-col pt-38 sm:pt-0'>
                                {/* Push heading down */}
                                <div
                                    aria-hidden={true}
                                    className='hidden sm:block sm:w-5/8 lg:w-3/8 text-transparent type-style-heading select-none pointer-events-none'>
                                    {page?.title}
                                </div>
                                <ul className='flex flex-col gap-y-38 sm:gap-y-48'>
                                    {studios.map((studio) => (
                                        <li
                                            className='scroll-mt-70 sm:scroll-mt-110'
                                            id={studio.slug}
                                            key={studio.id}>
                                            <article className='expand-interaction'>
                                                <h2 className='type-style-heading'>
                                                    <a
                                                        className='underline expand-interaction__action'
                                                        href={studio.url}>
                                                        {studio.title}
                                                    </a>
                                                </h2>

                                                <p className='mt-8 type-style-body'>
                                                    {formatStudioCapacity(studio)}
                                                </p>

                                                {studio.listingText && (
                                                    <p className='type-style-body'>
                                                        {studio.listingText}
                                                    </p>
                                                )}

                                                {studio.listingImage && (
                                                    <figure className='mt-16 text-[0] leading-[0] tracking-normal pointer-events-none select-none'>
                                                        <Image {...studio.listingImage} />
                                                    </figure>
                                                )}
                                            </article>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </PageShell>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default StudioListingPage;
